import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";
import { generateText } from "ai";
import { getTutorModel } from "./ai-gateway.server";

const MODEL = "google/gemini-3-flash-preview";

export const summarizeLibraryResource = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { resourceId: string; force?: boolean }) =>
    z.object({ resourceId: z.string().uuid(), force: z.boolean().optional() }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const sb = context.supabase;
    if (!data.force) {
      const { data: cached } = await sb
        .from("library_summaries")
        .select("summary,key_points,model,updated_at")
        .eq("resource_id", data.resourceId)
        .maybeSingle();
      if (cached) return cached;
    }
    const { data: r, error } = await sb
      .from("library_resources")
      .select("title,description,author,kind,topic,tags,language,pages,duration_seconds")
      .eq("id", data.resourceId)
      .maybeSingle();
    if (error || !r) throw new Error(error?.message ?? "Resource not found");

    const prompt = `You are MAKAI AI summarising a school library resource for a learner in Uganda.
Resource:
- Title: ${r.title}
- Kind: ${r.kind}
- Author: ${r.author ?? "Unknown"}
- Topic: ${r.topic ?? "N/A"}
- Tags: ${(r.tags ?? []).join(", ") || "N/A"}
- Description: ${r.description ?? "N/A"}

Return STRICT JSON with this shape:
{ "summary": "2-3 sentence overview, plain English", "key_points": ["point 1", "point 2", "point 3", "point 4", "point 5"] }
Keep it concise, NCDC-aligned, encouraging tone. No markdown.`;

    const { text } = await generateText({ model: getTutorModel(MODEL), prompt });
    let parsed: { summary: string; key_points: string[] };
    try {
      const cleaned = text.replace(/^```json\s*/i, "").replace(/```$/i, "").trim();
      parsed = JSON.parse(cleaned);
    } catch {
      parsed = { summary: text.slice(0, 600), key_points: [] };
    }

    const { data: row, error: upErr } = await sb
      .from("library_summaries")
      .upsert(
        {
          resource_id: data.resourceId,
          summary: parsed.summary,
          key_points: parsed.key_points,
          generated_by: context.userId,
          model: MODEL,
        },
        { onConflict: "resource_id" },
      )
      .select("summary,key_points,model,updated_at")
      .single();
    if (upErr) throw new Error(upErr.message);
    return row;
  });
